import React, {useMemo} from 'react';

import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Image} from '@udemy/react-core-components';
import {GwgSubscriptionCTA} from '@udemy/subscription-browse';
import {
    useConsumerSubscriptionGwGPlan,
    useConsumerSubscriptionGwGPlusPersonalPlan,
    useActiveSubscriptions,
} from '@udemy/subscription-graphql';

import {isSubscriberToAnyGwgPlan} from 'src/features/google-ai-plan-page/utils/is-gwg-subscriber';
import {GetGrowWithGoogleEnrollmentsQuery} from 'src/generated/graphql';

import styles from './hero-banner.module.less';

/** Minimum total enrollments required to display the learners count in the hero. */
const MIN_ENROLLMENTS_TO_DISPLAY = 1000;

export interface HeroBannerProps {
    title?: string;
    enrollmentsData?: GetGrowWithGoogleEnrollmentsQuery;
}

export function HeroBanner({title, enrollmentsData}: HeroBannerProps) {
    const {gettext} = useI18n();
    const isMobileMax = useMatchMedia('sm-max');
    const combinedGwgPlan = useConsumerSubscriptionGwGPlusPersonalPlan();
    const growWithGooglePlan = useConsumerSubscriptionGwGPlan();
    const activeSubscription = useActiveSubscriptions()[0];
    const isGwgSubscriber = isSubscriberToAnyGwgPlan(activeSubscription);

    // Formatted total enrollments string, or null when data is missing or below threshold.
    // NOTE: Currently we're not collecting lifetime subscribers, so we will never display the learners count, until we do.
    // Leaving the code here for future reference.
    // Slack thread: https://udemy.slack.com/archives/C09URRFU43F/p1770307868844889
    const learnersCount = useMemo(() => {
        const items = enrollmentsData?.careerAccelerators?.items;
        if (!items) {
            return null;
        }

        const totalEnrollments = items.reduce(
            (sum, accelerator) => sum + (accelerator.stats?.enrollments?.count ?? 0),
            0,
        );

        if (totalEnrollments < MIN_ENROLLMENTS_TO_DISPLAY) {
            return null;
        }

        return totalEnrollments.toLocaleString(undefined, {
            notation: 'compact',
            compactDisplay: 'short',
        });
    }, [enrollmentsData]);

    return (
        <section className={styles['hero-wrapper']}>
            <div className={styles['hero-content']}>
                {/* Google branding */}
                <div className={styles['branding']}>
                    <Image
                        src="https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/grow-with-google/google-logo.svg"
                        alt="Google"
                        className={styles['logo']}
                        width={100}
                        height={32}
                    />
                </div>

                {/* Title */}
                <h1 className={styles['hero-title']}>{title ?? gettext('Learn AI with Google')}</h1>

                {/* Description */}
                <p className={styles['hero-description']}>
                    {gettext(
                        'Build in-demand AI skills and earn a Google certificate to prove your AI fluency — all in one plan.',
                    )}
                </p>

                {/* CTA Section */}
                <div className={styles['cta-container']}>
                    {!isGwgSubscriber && (
                        <GwgSubscriptionCTA
                            plan={growWithGooglePlan}
                            offerCtaStyle="primary"
                            combinedGwgPlan={combinedGwgPlan}
                            showSubtitle={false}
                            uiRegion="google-ai-plan-hero"
                            className={styles['cta-button']}
                            size={isMobileMax ? 'medium' : 'large'}
                            currentlySubscribedPlan={activeSubscription}
                        />
                    )}

                    {/* Learners count */}
                    {learnersCount && (
                        <div className={styles['learners-info']}>
                            <Icon color="inherit" glyph="people" size="xsmall" label={false} />
                            <span>
                                <span className={styles['learners-count']}>{learnersCount}</span>{' '}
                                {gettext('learners already enrolled')}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
